import { PlanTripDialog } from '#components'
import {
	DialogController,
	PredefinedDialog,
	PredefinedDialogType,
} from '~/components/passporterUi/Dialog/dialogController'
import { Itinerary2, TRIP_PHASE } from '~/models/Itinerary'
import ItineraryGroup from '~/models/ItineraryGroup'
import { Page } from '~/models/PaginatedItems'
import { Spot2 } from '~/models/Spot'
import { ItineraryService } from '~/passporter-services/itinerary/service'
import EventsService from '~/services/events'
import { TranslationService } from '~/services/Translation'
import { useItineraryStore } from '~/store/itinerary'
import { useProfileStore } from '~/store/profile'
import { useProfileTrips } from '~/store/profileTrips'
import { TripMetric } from '../metrics/trip'

export default class TripActions {
	private static get itineraryStore() {
		return useItineraryStore()
	}

	private static get profileStore() {
		return useProfileStore()
	}

	private static get profileTripsStore() {
		return useProfileTrips()
	}

	public static async createTrip(
		itinerary: Itinerary2,
		recommendedSpots?: boolean,
		origin?: string
	): Promise<Itinerary2> {
		try {
			const trip = await ItineraryService.createItinerary(itinerary, recommendedSpots, origin)
			this.profileTripsStore.resetAll()
			this.profileStore.resetDestinations()
			TripMetric.createTrip(itinerary)
			return trip
		} catch (e) {
			console.error(e)
			return {} as Itinerary2
		}
	}

	public static async editTrip(itinerary: Itinerary2): Promise<Itinerary2> {
		this.profileTripsStore.resetAll()
		try {
			const edited = await ItineraryService.editItinerary(itinerary)
			this.itineraryStore.addItinerary(edited)
			return edited
		} catch {
			return {} as Itinerary2
		}
	}

	public static async deleteItinerary(itineraryId: string) {
		try {
			await ItineraryService.deleteItinerary(itineraryId)
			this.profileTripsStore.resetAll()
			EventsService.emitSuccess('common.action.remove.toast.success')
		} catch (e) {
			console.error(e)
			EventsService.emitError('common.action.remove.toast.error')
		}
	}

	public static createDeleteItineraryDialog(): DialogController {
		const translation: TranslationService = TranslationService.getInstance()
		const title: string = translation.translate('Common_Deleteitinerary_button')
		const body: string = translation.translate('B2C_edittravel_question_deletetravel_description')
		const deleteDialogSetup = new PredefinedDialog(PredefinedDialogType.DELETE_ALERT)
			.title(title)
			.bodyText(body)
			.size('s')
			.build()
		return new DialogController(deleteDialogSetup)
	}

	public static createTripDialog(trip?: Itinerary2) {
		const tripDialogSetup = new PredefinedDialog(PredefinedDialogType.EMPTY, PlanTripDialog)
			.title(TranslationService.getInstance().translate('web.edit.trip.header'))
			.size('s')
			.mobileType('fullScreen')

		if (trip) {
			tripDialogSetup.props({ trip })
		}
		return new DialogController(tripDialogSetup.build())
	}

	public static validateTrip = async (trip: Itinerary2) => {
		const errors = await ItineraryService.validateItinerary(trip)
		if (!errors) return
		return this.translateErrors(errors)
	}

	public static async createGroup(
		itineraryId: string,
		group: ItineraryGroup
	): Promise<{ group: ItineraryGroup | undefined; groups: ItineraryGroup[] }> {
		const translation = TranslationService.getInstance()
		try {
			const response = await ItineraryService.createGroup(itineraryId, group)
			const groups = await ItineraryService.getGroups(itineraryId)
			EventsService.emitSuccess(translation.translate('web.my_places.toast.tag_created.success'))
			return { group: response, groups }
		} catch {
			EventsService.emitError(translation.translate('web.my_places.toast.tag_created.error'))
			return { group: undefined, groups: [] }
		}
	}

	public static async editGroup(itineraryId: string, groupId: string, group: ItineraryGroup) {
		const translation = TranslationService.getInstance()
		try {
			await ItineraryService.editGroup(itineraryId, groupId, group)
			const groups = await ItineraryService.getGroups(itineraryId)
			EventsService.emitSuccess(translation.translate('web.my_places.toast.tag_edited.success'))
			return groups
		} catch {
			EventsService.emitError(translation.translate('web.my_places.toast.tag_edited.error'))
		}
	}

	public static deleteCurrentGroupDialog() {
		const deleteDialogSetup = new PredefinedDialog(PredefinedDialogType.DELETE_ALERT)
			.title(TranslationService.getInstance().translate('web.delete_tag_modal.title'))
			.bodyText(TranslationService.getInstance().translate('web.delete_tag_modal.description'))
			.size('s')
			.build()
		return new DialogController(deleteDialogSetup)
	}

	public static async deleteCurrentGroup(itineraryId: string, groupId: string) {
		const translation = TranslationService.getInstance()
		try {
			await ItineraryService.deleteGroup(itineraryId, groupId)
			const groups = await ItineraryService.getGroups(itineraryId)
			EventsService.emitSuccess(translation.translate('web.my_places.toast.tag_deleted.success'))
			return groups
		} catch {
			EventsService.emitError(translation.translate('web.my_places.toast.tag_deleted.error'))
		}
	}

	public static async recommendedSpots(itineraryId: string): Promise<Spot2[]> {
		try {
			const stored: Spot2[] | undefined = this.itineraryStore.retrieveRecommendedSpots(itineraryId)
			if (stored?.length) return stored

			const spots = await ItineraryService.getRecommendedSpots(itineraryId)
			this.itineraryStore.addRecommendedSpots(itineraryId, spots)
			return spots
		} catch {
			return []
		}
	}

	public static async getMostSavedSpots(
		itineraryId: string,
		page: number = 0,
		pageSize: number = 10
	): Promise<Page<Spot2>> {
		try {
			return await ItineraryService.getMostSavedSpots(itineraryId, page, pageSize)
		} catch {
			return { items: [] }
		}
	}

	static async changeCover(
		itineraryId: string,
		coverMediaId: string | null,
		coverUrl?: string
	): Promise<Itinerary2> {
		const t: TranslationService = TranslationService.getInstance()
		try {
			let itinerary = await ItineraryService.editItineraryCover(itineraryId, coverMediaId)
			if (coverUrl) itinerary = { ...itinerary, cover: coverUrl }
			const successKey = coverMediaId
				? 'photoUpload_successFeedback_uploadSuccessful'
				: 'removePhoto_successFeedback_deleteSuccessful'

			EventsService.emitSuccess(t.translate(successKey))
			this.profileTripsStore.resetLastUpdatedTrip()
			this.itineraryStore.removeItinerary(itineraryId)
			this.itineraryStore.addItinerary(itinerary)
			return itinerary
		} catch (e) {
			console.error(e)
			const errorKey = coverMediaId
				? 'photoUpload_errorFeedback_errorUpload'
				: 'removePhoto_errorFeedback_deleteError'

			EventsService.emitError(t.translate(errorKey))
			return {} as Itinerary2
		}
	}

	private static translateErrors(
		errors: Partial<Record<keyof Itinerary2, string>>
	): Partial<Record<keyof Itinerary2, string>> {
		const translated: Partial<Record<keyof Itinerary2, string>> = {
			name: errors.name && this.translateNameError(errors.name),
			destinations: errors.destinations && this.translateDestinationsError(errors.destinations),
		}
		return translated
	}

	private static translateNameError(error: string): string {
		const translation: TranslationService = TranslationService.getInstance()
		const errors: Record<string, string> = {
			empty_value: translation.translate('input_errorMessage_fieldRequired') as string,
			invalid_format: translation.translate('name_description_minimumCharacters') as string,
		}
		return errors[error] || errors[0]!
	}

	private static translateDestinationsError(error: string): string {
		const translation: TranslationService = TranslationService.getInstance()
		const errors: Record<string, string> = {
			empty_value: translation.translate('input_errorMessage_fieldRequired') as string,
		}
		return errors[error] || errors[0]!
	}

	static async getTrips(
		phase: TRIP_PHASE,
		userId: string,
		page: number,
		count: number
	): Promise<Page<Itinerary2>> {
		try {
			const pageSize = 16
			const itemsRequested = (page + 1) * pageSize
			const stored = this.profileTripsStore.getTrips(phase)
			if (stored.length >= itemsRequested) {
				const totalItems = count > 0 ? count : stored.length
				const next = Math.trunc(totalItems / pageSize) - page
				return {
					items: stored,
					count: totalItems,
					next,
				}
			}
			const response = await ItineraryService.getTrips(phase, userId, page, pageSize)
			this.profileTripsStore.addTrips(phase, response.items)
			return response
		} catch (error) {
			console.error(error)
			return {
				items: [],
			}
		}
	}
}
